import MealPlanHero from "@/components/MealPlanHero";
import Module from "@/components/Module";
import SimilarEntries from "@/components/SimilarEntries";
import { useAdContext } from "@/contexts/AdContext";
import { useHeaderContext } from "@/contexts/HeaderContext";
// import useSendConversionEventToAlgolia from "@/hooks/useSendConversionEventToAlgolia";
import craft from "@/lib/craft";
import getCommonStaticProps from "@/lib/getCommonStaticProps";
import QueryMealPlan from "@/queries/QueryMealPlan.graphql";
import QueryMealPlans from "@/queries/QueryMealPlans.graphql";
import QueryMealPlansIndex from "@/queries/QueryMealPlansIndex.graphql";
import QuerySlugs from "@/queries/QuerySlugs.graphql";
import AdRosTop from "@/src/ads/components/AdRosTop";
import createBreadcrumbsJsonLd from "@/src/recipes/lib/createBreadcrumbsJsonLd";
import AboutMobPremiumBlock from "@components/AboutMobPremiumBlock";
import TestimonialsSection from "@components/TestimonialsSection";
import clsx from "classnames";
import Head from "next/head";
import { useEffect } from "react";
import { Seomatic } from "react-seomatic";

export async function getStaticPaths() {
  const response = await craft([
    {
      document: QuerySlugs,
      variables: {
        section: "mealPlans",
        limit: process.env.VERCEL_ENV !== "production" ? 1 : 1,
      },
    },
  ]);

  const { entries } = response[0] || {};

  const paths = entries.map((entry) => ({
    params: { slug: entry.slug },
  }));

  return { paths, fallback: "blocking" };
}

export async function getStaticProps({ params, previewData, preview }) {
  // Fetch common static properties
  const commonStaticProps = await getCommonStaticProps();

  const { slug } = params;

  const queries = [
    {
      document: QueryMealPlansIndex,
    },
    { document: QueryMealPlans },
    {
      document: QueryMealPlan,
      variables: {
        slug,
      },
    },
  ];

  const response = await craft(queries, preview ? previewData.token : null);

  const { pageEntry } = response[0];
  const { mealPlans } = response[1];
  const { mealPlan } = response[2];

  if (!mealPlan) {
    return {
      props: {
        ...commonStaticProps,
      },
      notFound: true,
    };
  }

  const breadcrumbs = [
    { label: "Meal Plans", href: "/meal-plans" },
    { label: mealPlan.title, href: null },
  ];

  const jsonLdSchema = [createBreadcrumbsJsonLd(breadcrumbs)];

  return {
    props: {
      ...commonStaticProps,
      pageEntry,
      mealPlans,
      mealPlan,
      breadcrumbs,
      seomatic: mealPlan.seomatic,
      preview: preview ?? false,
      jsonLdSchema,
    },
    revalidate: process.env.VERCEL_ENV !== "production" ? 1 : 3600,
  };
}

const MealPlanEntry = ({
  mealPlan,
  preview = false,
  premiumPromotion,
  testimonials = [],
}) => {
  // Set showAds to true for this page
  const { setShowAds } = useAdContext();
  useEffect(() => setShowAds(true));

  const variant = mealPlan.planVariants?.[0];

  // useSendConversionEventToAlgolia(mealPlan); // TODO: no longer sending events to algolia; remove entirely eventually

  const ingredientsIntroduction =
    variant?.ingredientsIntroduction || mealPlan.ingredientsIntroduction;

  const ingredientLists = !!variant
    ? [{ ingredients: variant?.ingredients }]
    : mealPlan.ingredientLists;

  const recipesIntroduction =
    variant?.recipesIntroduction || mealPlan.recipesIntroduction;

  const recipes = variant?.recipes || mealPlan.recipes;

  const testimonialsToDisplay = testimonials?.filter((testimonial) =>
    testimonial.showTestimonialOnPages.includes("individualMealPlan")
  );

  // Show the header border for this page
  const { setShowBorder } = useHeaderContext();
  useEffect(() => {
    setShowBorder(true);
    return () => setShowBorder(false);
  }, []);

  return (
    <>
      <Seomatic Head={Head} {...mealPlan.seomatic} />

      <div className={clsx("Plan", "")}>
        <div className={clsx("Plan__hero", "relative border-b print:hidden")}>
          <MealPlanHero
            mealPlan={mealPlan}
            eyebrow={"See all plans"}
            eyebrowHref={`/meal-plans`}
            heading={mealPlan.title}
            introduction={mealPlan.introduction}
            images={mealPlan.images}
          />
        </div>

        <AdRosTop />

        <div className={clsx("Plan__content", "")}>
          {ingredientLists && ingredientLists.length > 0 && (
            <div
              className={clsx("Plan__ingredients", "bg-zinc-100 py-6 lg:py-12")}
            >
              <_Ingredients
                introduction={ingredientsIntroduction}
                ingredientLists={ingredientLists}
                servingInformation={
                  mealPlan.servingInformation ||
                  "Makes 3 dinners, each serving 4"
                }
              />
            </div>
          )}
          {mealPlan.batchPlan && (
            <div className={clsx("Plan__baseRecipe", "border-b py-6 lg:py-12")}>
              <_BaseRecipe
                mealPlan={mealPlan}
                introduction={mealPlan.baseRecipeIntroduction}
                recipe={mealPlan.baseRecipe?.[0]}
              />
            </div>
          )}
          <div className={clsx("Plan__recipes", "py-6 lg:py-12")}>
            <_Recipes
              mealPlan={mealPlan}
              introduction={recipesIntroduction}
              recipes={recipes}
              premiumPromotion={premiumPromotion}
            />
          </div>
        </div>

        <div className={clsx("border-t-2 py-6 lg:py-8 xl:py-12")}>
          <SimilarEntries entryId={mealPlan.id} />
        </div>

        <TestimonialsSection testimonials={testimonialsToDisplay} />
      </div>
    </>
  );
};

const _Ingredients = ({
  introduction,
  ingredientLists,
  servingInformation,
}) => {
  return (
    <div className={clsx("MealPlansIndex__ingredients", "container relative")}>
      <div className={clsx("grid grid-cols-12 sm:gap-6")}>
        <div className={clsx("col-span-12 lg:col-span-4")}>
          <div className={clsx("mb-6 space-y-4 pr-12")}>
            <h2 className={clsx("heading-md--thin z-10")}>Ingredients</h2>

            <div className={clsx("font-body text-base text-zinc-400")}>
              {servingInformation}
            </div>

            <div
              className={clsx("body-text")}
              dangerouslySetInnerHTML={{ __html: introduction }}
            ></div>
          </div>
        </div>
        <div className={clsx("col-span-12 lg:col-span-8")}>
          <_IngredientLists lists={ingredientLists} />
        </div>
      </div>
    </div>
  );
};

const _IngredientLists = ({ lists }) => {
  return (
    <div className={clsx("py-3")}>
      {lists.map((list) => {
        return (
          <div key={list.id} className={clsx("mb-6 last:mb-0 lg:mb-12")}>
            {!!list?.heading && (
              <h3
                className={clsx(
                  "mb-2 flex max-w-sm items-center px-0 text-base text-zinc-950 lg:text-lg"
                )}
              >
                {list?.heading}
              </h3>
            )}
            <div className={clsx("grid grid-cols-12 gap-x-4 lg:gap-x-6")}>
              {list?.ingredients.map((item) => {
                return (
                  <div
                    key={item.id}
                    className={clsx("group col-span-12 md:col-span-6")}
                  >
                    <_ListItem item={item} />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const _ListItem = ({ item }) => {
  return (
    <div
      className={clsx(
        "h-component group relative z-0 flex h-full w-full items-center border-b border-zinc-200 px-0 transition group-last:border-b-0 focus:border-zinc-200 lg:group-last:border-b"
      )}
    >
      <div
        className={clsx(
          "pr-6 text-left font-body text-sm text-zinc-800 lg:text-base"
        )}
      >
        {item.label}
      </div>
    </div>
  );
};

const _BaseRecipe = ({ mealPlan, recipe, introduction }) => {
  return (
    <div
      className={clsx("MealPlansIndex__baseRecipes", "space-y-6 lg:space-y-12")}
    >
      <div className={clsx("container")}>
        <div className={clsx("grid grid-cols-12 sm:gap-6")}>
          <div className={clsx("col-span-12 lg:col-span-4")}>
            <div className={clsx("space-y-4")}>
              <h2 className={clsx("heading-md--thin")}>The Base Recipe</h2>
            </div>
          </div>
          <div className={clsx("col-span-12 lg:col-span-8")}>
            <div
              className={clsx("body-text-lg pt-3 lg:pr-12")}
              dangerouslySetInnerHTML={{ __html: introduction }}
            ></div>
          </div>
        </div>
      </div>
      <div className={clsx("space-y-6 lg:space-y-12")}>
        <div key={recipe.id} className={clsx("max-screen-w-2xl container")}>
          <Module
            image={recipe.image?.[0]}
            href={`/meal-plans/${mealPlan.slug}/${recipe.slug}`}
            heading={recipe.title}
            text={recipe.summary}
            buttonLabel="See recipe"
            imagePosition={"left"}
          />
        </div>
      </div>
    </div>
  );
};

const _Recipes = ({
  heading = "The Recipes",
  mealPlan,
  introduction,
  recipes,
  premiumPromotion,
}) => {
  return (
    <div className={clsx("MealPlansIndex__recipes", "space-y-6 lg:space-y-12")}>
      <div className={clsx("container")}>
        <div className={clsx("grid grid-cols-12 sm:gap-6")}>
          <div className={clsx("col-span-12 lg:col-span-4")}>
            <div className={clsx("space-y-4")}>
              <h2 className={clsx("heading-md--thin")}>{heading}</h2>
            </div>
          </div>
          <div className={clsx("col-span-12 lg:col-span-8")}>
            <div
              className={clsx("body-text-lg pt-3 lg:pr-12")}
              dangerouslySetInnerHTML={{ __html: introduction }}
            ></div>
          </div>
        </div>
      </div>
      <div className={clsx("space-y-6 lg:space-y-12")}>
        {recipes?.map((recipe, _i) => {
          return (
            <div key={recipe.id}>
              <div
                key={recipe.id}
                className={clsx("max-screen-w-2xl container")}
              >
                <Module
                  image={recipe.image?.[0]}
                  href={`/meal-plans/${mealPlan.slug}/${recipe.slug}`}
                  heading={recipe.title}
                  text={recipe.summary}
                  index={_i}
                  buttonLabel="See recipe"
                  imagePosition={_i % 2 !== 0 ? "right" : "left"}
                />
              </div>
              {(_i === 1 ||
                (_i === recipes.length - 1 && recipes.length === 1)) && (
                <div key="about-mob-premium-block" className="mt-6 lg:mt-12">
                  <AboutMobPremiumBlock blockData={premiumPromotion} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MealPlanEntry;
